/** Etichetele unui tronson: chipurile puse pe el, plus câmpul care adaugă.
 *
 *  CE SUNT. Cuvintele proiectului puse pe module — «social», «studio»,
 *  «varianta clientului». Colecția se poate grupa azi doar după categoria pe
 *  care serverul o derivă din `role` (capăt, mijloc, colț), iar aia spune unde
 *  poate sta modulul pe bară, nu la ce e bun. Cuvintele astea sunt ale
 *  proiectului și ale oamenilor lui, deci nu pot fi o enumerare scrisă în cod.
 *
 *  SE ALEGE DIN VOCABULAR SAU SE SCRIE UNA NOUĂ, în același câmp și cu același
 *  gest. Un buton separat de «creează» ar fi cerut omului să știe dinainte dacă
 *  eticheta lui există — singurul lucru pe care tocmai lista din fața lui i-l
 *  spune. Ce scrie filtrează vocabularul; dacă nu se potrivește nimic, Enter o
 *  naște.
 *
 *  DE CE NU SE POT ȘTERGE DIN VOCABULAR AICI. «×» scoate eticheta DE PE MODUL,
 *  nu din proiect — și trebuie să fie așa, fiindcă e gestul des. O etichetă
 *  scoasă de pe ultimul modul rămâne în listă, ca să fie aleasă, nu tastată din
 *  nou; asta e chiar rostul unui vocabular.
 *
 *  NU CITEȘTE ȘI NU SCRIE NIMIC SINGUR. Primește lista și dă înapoi mulțimea
 *  care trebuie să rămână; cererile le face ecranul care îl ține (colecția sau
 *  editorul), fiindcă tot el știe ce are de reîmprospătat după — cartonașele,
 *  în colecție.
 */
import { useState } from "react";
import { useT } from "../../i18n";
import type { Eticheta } from "../../api";
import "./etichete.css";

/** Câte încap pe un modul — aceeași cifră ca `MAX_ETICHETE` din
 *  `control-plane/api/catalog.py`. Nu e o regulă a catalogului, e una a
 *  cartonașului: peste vreo zece, rândul de chipuri e mai lung decât tot restul
 *  și nu se mai citește niciuna. Scrisă și aici ca butonul să se stingă înainte
 *  ca serverul să refuze — un refuz pentru o limită pe care ecranul o știe e un
 *  drum degeaba. */
export const MAX_ETICHETE = 20;

/** Numele, cum îl compară serverul: spațiile strânse, fără margini. Aceeași
 *  curățare ca `_curata` de-acolo — nu ca să ne încredem în ea (serverul o face
 *  oricum), ci ca «social » scris cu un spațiu în plus să nu pară o etichetă
 *  nouă în lista din fața omului. */
const curata = (nume: string) => nume.replace(/\s+/g, " ").trim().slice(0, 40);

export default function Etichete({
  etichete, posibile = [], doarCitire, laSchimbare, lucrez,
}: {
  /** Cele puse pe modul, în ordinea în care le întoarce serverul. */
  etichete: string[];
  /** Vocabularul proiectului — din ce se poate alege. Lipsă = nu se poate
   *  alege, doar scrie; e starea dinainte ca ecranul să-l fi citit. */
  posibile?: Eticheta[];
  doarCitire?: boolean;
  /** Mulțimea care trebuie să rămână. Lipsă = panoul doar arată. */
  laSchimbare?: (etichete: string[]) => void;
  /** O scriere e pe drum: butoanele se sting, ca două apăsări repezi să nu
   *  trimită două mulțimi care se încrucișează. */
  lucrez?: boolean;
}) {
  const t = useT();
  const [deschis, setDeschis] = useState(false);
  const [scris, setScris] = useState("");
  const potScrie = !!laSchimbare && !doarCitire;
  const mic = (s: string) => s.toLowerCase();

  /** Vocabularul, fără cele deja puse, filtrat cu ce s-a scris. */
  const deAles = posibile
    .filter((e) => !etichete.some((x) => mic(x) === mic(e.nume)))
    .filter((e) => !scris || mic(e.nume).includes(mic(curata(scris))));

  /** Ce s-a scris e un cuvânt nou? Atunci Enter îl naște. */
  const nou = curata(scris);
  const eNoua = !!nou
    && !posibile.some((e) => mic(e.nume) === mic(nou))
    && !etichete.some((x) => mic(x) === mic(nou));

  const pune = (nume: string) => {
    const curat = curata(nume);
    if (!curat || !laSchimbare) return;
    if (etichete.some((x) => mic(x) === mic(curat))) { setScris(""); return; }
    if (etichete.length >= MAX_ETICHETE) return;
    laSchimbare([...etichete, curat]);
    setScris("");
  };

  const scoate = (nume: string) => laSchimbare?.(
    etichete.filter((x) => mic(x) !== mic(nume)));

  return (
    <div className="ob-et">
      <div className="ob-et-randul">
        {etichete.map((nume) => (
          <span key={nume} className="ob-et-chip">
            {nume}
            {potScrie && (
              // `<span role="button">`, nu `<button>`: chipurile se desenează
              // și pe cartonașul din colecție, care E un buton — iar un buton
              // în alt buton e HTML pe care browserele îl repară cum vor.
              <span className="ob-et-scoate" role="button" tabIndex={0}
                    title={t("etichete.scoate_titlu", { nume })}
                    onClick={(e) => { e.stopPropagation(); scoate(nume); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation(); scoate(nume);
                      }
                    }}>×</span>
            )}
          </span>
        ))}
        {!etichete.length && !deschis && (
          <span className="ob-et-niciuna">{t("etichete.niciuna")}</span>
        )}
        {potScrie && !deschis && etichete.length < MAX_ETICHETE && (
          <span className="ob-et-adauga" role="button" tabIndex={0}
                title={t("etichete.adauga_titlu")}
                onClick={(e) => { e.stopPropagation(); setDeschis(true); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.stopPropagation(); setDeschis(true); }
                }}>
            {t("etichete.adauga")}
          </span>
        )}
        {potScrie && etichete.length >= MAX_ETICHETE && (
          <span className="ob-et-plin">
            {t("etichete.plin", { n: String(MAX_ETICHETE) })}
          </span>
        )}
      </div>

      {potScrie && deschis && (
        <div className="ob-et-alegere">
          <input className="inp ob-et-camp" autoFocus value={scris}
                 disabled={lucrez}
                 placeholder={t("etichete.cauta")}
                 onChange={(e) => setScris(e.target.value)}
                 onKeyDown={(e) => {
                   e.stopPropagation();
                   // ENTER PUNE PRIMA POTRIVIRE, nu neapărat ce s-a scris: cine
                   // tastează «soc» și apasă Enter vrea «social» din listă, nu o
                   // etichetă nouă numită «soc». Se naște una nouă doar când
                   // nimic din vocabular nu se potrivește.
                   if (e.key === "Enter") {
                     if (deAles.length) pune(deAles[0].nume);
                     else if (eNoua) pune(nou);
                   }
                   if (e.key === "Escape") { setScris(""); setDeschis(false); }
                 }} />
          <span className="ob-et-gata" role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setScris(""); setDeschis(false); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { setScris(""); setDeschis(false); }
                }}>
            {t("etichete.gata")}
          </span>
          <div className="ob-et-lista">
            {deAles.map((e) => (
              <span key={e.id} className="ob-et-propusa" role="button" tabIndex={0}
                    onClick={(ev) => { ev.stopPropagation(); pune(e.nume); }}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter") { ev.stopPropagation(); pune(e.nume); }
                    }}>
                {e.nume}
                {/* Pe câte module e pusă: o etichetă cu 0 se vede că a rămas
                    în urmă, iar una cu 6 se vede că e cea folosită. */}
                <i className="ob-et-cate">{e.module}</i>
              </span>
            ))}
            {eNoua && (
              <span className="ob-et-propusa ob-et-noua" role="button" tabIndex={0}
                    onClick={(ev) => { ev.stopPropagation(); pune(nou); }}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter") { ev.stopPropagation(); pune(nou); }
                    }}>
                {t("etichete.creeaza", { nume: nou })}
              </span>
            )}
            {!deAles.length && !eNoua && (
              // DOUĂ TĂCERI DIFERITE, două propoziții. «N-ai nicio etichetă»
              // e starea unui proiect nou; «toate sunt deja pe modul» e a unuia
              // în care vocabularul e viu — iar prima, scrisă în locul celei de
              // a doua, spune omului că a pierdut cuvintele pe care tocmai le
              // vede pe chipuri.
              <span className="ob-et-goala">
                {t(posibile.length ? "etichete.toate_puse"
                                   : "etichete.nimic_de_ales")}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
